import React, { ReactElement } from 'react';
import { Activite } from '../../../../../../common/src/entities';
import { Document, Image, Link, Page, StyleSheet, Text, View, Font } from '@react-pdf/renderer';
import inter from '@fontsource/inter/files/inter-latin-400-normal.woff';
import inter500 from '@fontsource/inter/files/inter-latin-500-normal.woff';
import interBold from '@fontsource/inter/files/inter-latin-700-normal.woff';
import config from '../../../../Utils/config';
import logo from '../../../../../public/SA_horizontal_couleur_HR.png';
import '../MenuFiltre.scss';
import { Section, Separateur } from './Sections';
import { getSections } from '../Activite/Sections';
import { DateTime } from 'luxon';
import { getImagePDFUrl } from '../../../../Utils/getFileURL';

Font.register({
    family: 'Inter',
    fonts: [{ src: inter }, { src: inter500, fontWeight: 500 }, { src: interBold, fontWeight: 700 }],
});

export function AjusterSautsDeLigne(texte, longueurMax) {
    if (typeof texte !== 'string') return ''; // Vérifiez si le texte est une chaîne de caractères

    const mots = texte.split(' ');
    let ligneActuelle = '';
    let resultat = '';

    for (const mot of mots) {
        if ((ligneActuelle + mot).length > longueurMax) {
            resultat += ligneActuelle.trim() + '\n';
            ligneActuelle = mot + ' ';
        } else {
            ligneActuelle += mot + ' ';
        }
    }

    resultat += ligneActuelle.trim();
    return resultat;
}

export default function ActivitePDF({
    activite,
    masquerCovid,
}: {
    activite: Activite;
    masquerCovid: boolean;
}): ReactElement {
    const sections = getSections(activite);
    if (sections[0].champs) {
        sections[0].champs.pop();
    }
    sections[1].noHr = false;

    const styles = StyleSheet.create({
        body: {
            fontFamily: 'Inter',
            padding: '2cm',
            fontSize: 12,
            textAlign: 'justify',
        },
        page: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#333',
            backgroundColor: '#FFF',
            top: -25,
            marginBottom: 5,
        },
        logo: { top: -15 },

        coverTitle: {
            fontSize: 32,
            fontWeight: 'bold',
        },
        h1: {
            width: '100%',
            fontSize: 24,
            textAlign: 'center',
        },
        imageContainer: {
            width: '100%',
            height: 525,
            overflow: 'hidden',
        },
        image: {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        },
        fixed: {
            position: 'absolute',
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'grey',
        },
        header: {
            top: 13,
        },
        header2: {
            top: 16,
            fontSize: 10,
        },
        footer: {
            fontSize: 10,
            position: 'absolute',
            bottom: 20,
            right: 20,
        },
        watermark: {
            position: 'absolute',
            alignSelf: 'center',
            transform: 'rotate(45deg)',
            fontSize: 45,
            top: '50%',
            color: '#f4f4f4',
        },
        pillsContainer: {
            flexDirection: 'row',
            flexWrap: 'wrap', // This will wrap the pills to the next line if they exceed the width of the parent container
            alignItems: 'center',
        },
        pill: {
            backgroundColor: '#ffffff',
            paddingVertical: 5,
            paddingHorizontal: 10,
            borderRadius: 20,
            fontSize: 8,
            borderColor: '#0088ff',
            borderWidth: 1,
            marginRight: 5,
            marginBottom: 5,
            marginTop: 5,
        },
        disclaimer: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        boldText: {
            fontSize: 12,
            fontWeight: 'bold',
            marginRight: 5,
        },
        normalText: {
            fontSize: 10,
        },
    });

    const location = window.location;
    const url = `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ''}`;
    function TronquerAvecEllipsis(texte, longueurMax) {
        return texte.length > longueurMax ? texte.slice(0, longueurMax - 3) + '...' : texte;
    }

    return (
        <Document>
            <Page style={styles.body}>
                <Text style={[styles.header, styles.fixed]} fixed>
                    Guide d&apos;activité - {TronquerAvecEllipsis(activite.name, 80)}
                </Text>
                <Text style={[styles.header2, styles.fixed]} fixed>
                    {'\n'} PDF créé :{' '}
                    {DateTime.local()
                        .setLocale('fr')
                        .toLocaleString({ month: 'numeric', day: 'numeric', year: 'numeric' })}
                    . Document à l&apos;usage exclusif des clients de Solution Animation.
                </Text>

                <Text style={[styles.footer, styles.fixed]} fixed>
                    Voir la liste des toutes les activités offertes : <Link src={url}>{url}</Link>
                    {'\n'}
                    Pour toutes questions : Isaac Barbe 514-942-5155 isaacbarbe@solutionanimation.com
                </Text>
                <Text
                    style={styles.footer}
                    render={({ pageNumber, totalPages }) => `Page ${pageNumber} de ${totalPages}`}
                    fixed
                />
                <Text style={styles.watermark} fixed>
                    Solution Animation
                </Text>
                <Image style={styles.logo} src={logo} />
                <View style={styles.page}>
                    <Text style={styles.coverTitle}>Guide d'activité</Text>
                    <Text style={styles.h1}>{activite.name}</Text>
                </View>

                {activite.fk_id_image_principale && (
                    <>
                        <View style={styles.imageContainer}>
                            <Image src={getImagePDFUrl(activite.fk_id_image_principale)} style={styles.image} />
                        </View>
                        {activite.fichiers && activite.fichiers.length > 1 && (
                            <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: '30' }}>
                                <Text>
                                    Voir plus d&apos;images ici :
                                    <Link
                                        src={`${url}/activites/${activite.id}`}
                                    >{`${url}/activites/${activite.id}`}</Link>
                                </Text>
                            </View>
                        )}
                    </>
                )}
                <View style={styles.disclaimer}>
                    <Text style={styles.boldText}>Étiquettes : </Text>
                    <Text style={styles.normalText}>
                        Présentés à des fins d'aperçu. Consultez le guide pour les clauses complètes.
                    </Text>
                </View>
                <View style={styles.pillsContainer}>
                    {activite.etiquettes.map((etiquette) =>
                        etiquette.est_public == 'oui' ? (
                            <View key={etiquette.id} style={styles.pill}>
                                <Text>{etiquette.name}</Text>
                            </View>
                        ) : null,
                    )}
                </View>
                <Separateur></Separateur>
                {sections.map((section) =>
                    section.titre === 'COVID-19' ? (
                        masquerCovid ? null : (
                            <Section key={section.titre} {...section} />
                        )
                    ) : (
                        <Section key={section.titre} {...section} />
                    ),
                )}
            </Page>
        </Document>
    );
}
